import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { Table } from '@components/common';
import UserImpersonationButton from '@components/admin/UserImpersonationButton/UserImpersonationButton';
import userService from '@services/userService';
import { useNotification } from '@hooks/useNotification';
import './UserManagement.scss';

const UserManagement = () => {
    const { t } = useLanguage();
    const notify = useNotification();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userService.getUsers();
            setUsers(response.models || []);
        } catch (err) {
            console.error('Failed to load users:', err);
            setError(t('admin.users.load_failed'));
            notify.error(t('admin.users.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            key: 'id',
            label: 'ID',
            sortable: true,
            width: '80px'
        },
        {
            key: 'name',
            label: t('admin.users.name'),
            sortable: true,
            render: (user) => (
                <button
                    className="user-name-link"
                    onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                    title={t('admin.users.edit_user')}
                >
                    {user.name || user.email || 'N/A'}
                </button>
            )
        },
        {
            key: 'email',
            label: t('admin.users.email'),
            sortable: true
        },
        {
            key: 'role',
            label: t('admin.users.role'),
            sortable: true,
            render: (user) => (
                <span className={`user-role user-role--${user.role?.toLowerCase()}`}>
                    {user.role}
                </span>
            )
        },
        {
            key: 'is_active',
            label: t('admin.users.status'),
            sortable: true,
            render: (user) => (
                <span className={`user-status ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? t('admin.users.active') : t('admin.users.inactive')}
                </span>
            )
        },
        {
            key: 'created_at',
            label: t('admin.users.created_at'),
            sortable: true,
            render: (user) => {
                if (!user.created_at) return '-';
                return new Date(user.created_at).toLocaleDateString();
            }
        },
        {
            key: 'actions',
            label: t('common.actions') || 'Actions',
            render: (user) => (
                <div className="user-actions">
                    <UserImpersonationButton
                        userId={user.id}
                        userName={user.name}
                        disabled={!user.is_active}
                    />
                </div>
            )
        }
    ];

    if (loading) {
        return (
            <div className="user-management">
                <div className="loading-state">
                    <p>{t('common.loading') || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-management">
                <div className="error-state">
                    <p>{error}</p>
                    <button onClick={loadUsers} className="retry-button">
                        {t('common.retry') || 'Retry'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="user-management">
            <div className="user-management__header">
                <h2>{t('admin.user_management') || 'User Management'}</h2>
                <p>{t('admin.user_management_desc') || 'Manage users and impersonate for troubleshooting.'}</p>
            </div>

            <div className="user-management__content">
                <Table
                    data={users}
                    columns={columns}
                    searchable={true}
                    searchPlaceholder={t('admin.users.search_placeholder')}
                    emptyMessage={t('admin.users.no_users')}
                    pagination={{
                        enabled: true,
                        pageSize: 20
                    }}
                />
            </div>
        </div>
    );
};

export default UserManagement;
